// src/components/AddClient.js - WITH AUTO-ENABLE PORTAL TOGGLE
import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc, Timestamp } from "firebase/firestore"; 
import {
  TextField,
  Button,
  Box,
  Stack,
  Alert,
  InputAdornment,
  IconButton,
  Collapse,
  Divider,
  Tooltip,
} from '@mui/material';

import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import BusinessIcon from '@mui/icons-material/Business';

// Import CustomerPortalToggle
import CustomerPortalToggle from './CustomerPortalToggle';

function AddClient({ onClientAdded }) {
  const [clientName, setClientName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});

  // Portal data state
  const [portalData, setPortalData] = useState({
    portalEnabled: true,
    portalEmail: '',
    portalPassword: ''
  });

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!clientName.trim()) {
      newErrors.clientName = 'Client name is required';
    }

    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      newErrors.contactEmail = 'Invalid email format';
    }

    if (contactPhone && !/^[0-9\s\-\+\(\)]{7,}$/.test(contactPhone)) {
      newErrors.contactPhone = 'Invalid phone number';
    }

    // Portal validation - if enabled, email is required
    if (portalData.portalEnabled && !portalData.portalEmail) {
      newErrors.portalEmail = 'Portal email is required when portal is enabled';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setMessage(null);
    
    // Validate
    if (!validateForm()) {
      setMessage({ 
        type: 'error', 
        text: 'Please fix the errors before submitting' 
      });
      return;
    }

    setIsLoading(true);

    try {
      const newClient = {
        name: clientName.trim(),
        contactEmail: contactEmail.trim() || null,
        contactPhone: contactPhone.trim() || null,
        address: address.trim() || null,
        createdAt: Timestamp.now(),
        
        // Add portal fields
        portalEnabled: portalData.portalEnabled,
        portalEmail: portalData.portalEnabled ? portalData.portalEmail : null,
        portalPassword: portalData.portalEnabled ? portalData.portalPassword : null,
        portalCreatedAt: portalData.portalEnabled ? Timestamp.now() : null,
      };

      const docRef = await addDoc(collection(db, "customers"), newClient);
      
      // Success message with portal info
      let successText = `✓ Client "${clientName}" created successfully!`;
      if (portalData.portalEnabled) {
        successText += ` Portal access enabled with credentials.`;
      }
      
      setMessage({ 
        type: 'success', 
        text: successText
      });

      // Reset form
      setClientName('');
      setContactEmail('');
      setContactPhone('');
      setAddress('');
      setErrors({});
      
      // Reset portal data (will auto-generate new password for next customer)
      setPortalData({
        portalEnabled: true,
        portalEmail: '',
        portalPassword: ''
      });

      // Callback if provided
      if (onClientAdded) {
        onClientAdded({ id: docRef.id, ...newClient });
      }

      // Auto-hide success message after 4 seconds
      setTimeout(() => setMessage(null), 4000);

    } catch (err) {
      console.error("Error adding client:", err);
      setMessage({ 
        type: 'error', 
        text: `Failed to create client: ${err.message}` 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setClientName('');
    setContactEmail('');
    setContactPhone('');
    setAddress('');
    setErrors({});
    setMessage(null);
    
    // Reset portal data
    setPortalData({
      portalEnabled: true,
      portalEmail: '',
      portalPassword: ''
    });
  };

  const isFormEmpty = !clientName && !contactEmail && !contactPhone && !address;

  return (
    <Box>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          {/* Client Name */}
          <TextField
            label="Client / Company Name"
            value={clientName}
            onChange={(e) => {
              setClientName(e.target.value);
              if (errors.clientName) {
                setErrors({ ...errors, clientName: null });
              }
            }}
            fullWidth
            required
            error={!!errors.clientName}
            helperText={errors.clientName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon fontSize="small" color={errors.clientName ? "error" : "action"} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused': {
                  '& .MuiInputAdornment-root svg': {
                    color: 'primary.main',
                  },
                },
              },
            }}
          />

          {/* Contact Email */}
          <TextField
            type="email"
            label="Contact Email"
            value={contactEmail}
            onChange={(e) => {
              setContactEmail(e.target.value);
              if (errors.contactEmail) {
                setErrors({ ...errors, contactEmail: null });
              }
            }}
            fullWidth
            error={!!errors.contactEmail}
            helperText={errors.contactEmail || "Optional - for invoices and communication"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon fontSize="small" color={errors.contactEmail ? "error" : "action"} />
                </InputAdornment>
              ),
            }}
          />

          {/* Contact Phone */}
          <TextField
            type="tel"
            label="Contact Phone"
            value={contactPhone}
            onChange={(e) => {
              setContactPhone(e.target.value);
              if (errors.contactPhone) {
                setErrors({ ...errors, contactPhone: null });
              }
            }}
            fullWidth
            error={!!errors.contactPhone}
            helperText={errors.contactPhone || "Optional - for quick contact"}
            placeholder="e.g., 01234 567890"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon fontSize="small" color={errors.contactPhone ? "error" : "action"} />
                </InputAdornment>
              ),
            }}
          />

          {/* Primary Address */}
          <TextField
            label="Primary Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            fullWidth
            multiline
            rows={2}
            helperText="Optional - main business/home address"
            placeholder="Street, City, Postcode"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                  <LocationOnIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Divider sx={{ my: 1 }} />

          {/* CUSTOMER PORTAL TOGGLE - NEW! */}
          <CustomerPortalToggle
            customerEmail={contactEmail}
            onPortalDataChange={(data) => setPortalData(data)}
            defaultEnabled={true}
            compact={false}
          />

          <Divider />

          {/* Action Buttons */}
          <Stack direction="row" spacing={1}>
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth 
              disabled={isLoading}
              startIcon={isLoading ? null : <CheckCircleIcon />}
              sx={{ 
                py: 1.2,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                },
              }}
            >
              {isLoading ? 'Creating...' : 'Create Client'}
            </Button>

            {!isFormEmpty && (
              <Tooltip title="Clear form">
                <IconButton 
                  onClick={handleClear}
                  disabled={isLoading}
                  sx={{ 
                    border: '1px solid rgba(148,163,184,0.3)',
                    '&:hover': {
                      bgcolor: 'rgba(248,113,113,0.1)',
                    },
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {/* Success/Error Message */}
          <Collapse in={!!message}>
            {message && (
              <Alert 
                severity={message.type}
                sx={{ 
                  animation: 'fadeIn 0.3s ease-in',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(-10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                {message.text}
              </Alert>
            )}
          </Collapse>
        </Stack>
      </Box>
    </Box>
  );
}

export default AddClient;